import dayjs from 'dayjs';
import { loadImageBase64 } from '../utils/loadImageBase64';

// กำหนด font สำหรับ pdfMake
const initializePdfMake = () => {
    if (window.pdfMake) {
        window.pdfMake.fonts = {
            THSarabunNew: {
                normal: 'THSarabunNew.ttf',
                bold: 'THSarabunNew Bold.ttf',
                italics: 'THSarabunNew Italic.ttf',
                bolditalics: 'THSarabunNew BoldItalic.ttf'
            }
        };
    }
};

// Helper function to get status text in Thai
const getExpenseStatusText = (status) => {
    const statusMap = {
        'pending': 'รอดำเนินการ',
        'approved': 'อนุมัติแล้ว',
        'paid': 'จ่ายแล้ว',
        'rejected': 'ไม่อนุมัติ'
    };
    return statusMap[status] || status;
};

/**
 * สร้าง PDF สำหรับตารางรายการค่าใช้จ่าย
 * @param {Array} expenses - รายการค่าใช้จ่ายทั้งหมดที่จะพิมพ์
 * @param {Object} company - ข้อมูลบริษัท
 * @param {Object} filters - ตัวกรองที่ใช้ (สำหรับแสดงในหัวเอกสาร)
 * @param {Object} stats - สถิติรวม
 * @returns {Promise<string>} - Data URL ของ PDF
 */
export const generateExpensesTablePDF = async (expenses, company = {}, filters = {}, stats = {}) => {
    // โหลด logo
    let logoBase64 = null;
    if (company?.logo_path) {
        try {
            logoBase64 = await loadImageBase64(`${process.env.REACT_APP_API_URL}/uploads/${company.logo_path}`);
        } catch (error) {
            console.warn('ไม่สามารถโหลด logo ได้:', error);
        }
    }

    // ตรวจสอบว่า pdfMake โหลดแล้วหรือยัง
    if (!window.pdfMake) {
        throw new Error('pdfMake library is not loaded');
    }

    // กำหนด font
    initializePdfMake();

    // สร้างข้อความแสดงช่วงเวลาที่กรอง
    let filterText = 'รายการทั้งหมด';
    if (filters.start_date && filters.end_date) {
        filterText = `ช่วงเวลา: ${dayjs(filters.start_date).format('DD/MM/YYYY')} - ${dayjs(filters.end_date).format('DD/MM/YYYY')}`;
    } else if (filters.start_date) {
        filterText = `ตั้งแต่: ${dayjs(filters.start_date).format('DD/MM/YYYY')}`;
    } else if (filters.end_date) {
        filterText = `ถึง: ${dayjs(filters.end_date).format('DD/MM/YYYY')}`;
    }

    // เพิ่มข้อมูลตัวกรองอื่นๆ
    const filterDetails = [];
    if (filters.search) filterDetails.push(`ค้นหา: "${filters.search}"`);
    if (filters.category) filterDetails.push(`หมวดหมู่: ${filters.category}`);
    if (filters.status) filterDetails.push(`สถานะ: ${getExpenseStatusText(filters.status)}`);
    if (filters.supplier_id) filterDetails.push(`กรองตามผู้จำหน่าย`);

    // สร้างตาราง
    const tableBody = [
        // Header row
        [
            { text: 'เลขที่เอกสาร', style: 'tableHeader', alignment: 'center' },
            { text: 'วันที่', style: 'tableHeader', alignment: 'center' },
            { text: 'ผู้จำหน่าย', style: 'tableHeader' },
            { text: 'หมวดหมู่', style: 'tableHeader' },
            { text: 'รายละเอียด', style: 'tableHeader' },
            { text: 'ยอดรวม', style: 'tableHeader', alignment: 'right' },
            { text: 'สถานะ', style: 'tableHeader', alignment: 'center' }
        ],
        // Data rows
        ...expenses.map(expense => {
            const totalAmount = parseFloat(expense.total_amount || 0);

            return [
                { text: expense.expense_number || '-', fontSize: 9 },
                { text: dayjs(expense.expense_date).format('DD/MM/YYYY'), fontSize: 9, alignment: 'center' },
                { text: expense.supplier_name || expense.supplier_company || '-', fontSize: 9 },
                { text: expense.category || '-', fontSize: 9 },
                { text: expense.description || '-', fontSize: 9 },
                { text: totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 }), fontSize: 9, alignment: 'right' },
                {
                    text: getExpenseStatusText(expense.status),
                    fontSize: 9,
                    alignment: 'center',
                    color: expense.status === 'approved' ? '#52c41a' : (expense.status === 'paid' ? '#1890ff' : (expense.status === 'rejected' ? '#ff4d4f' : '#faad14'))
                }
            ];
        })
    ];

    // เพิ่มแถวสรุปยอดรวม
    if (expenses.length > 0) {
        tableBody.push([
            { text: 'รวมทั้งสิ้น', bold: true, fontSize: 10, colSpan: 5, alignment: 'right' },
            {},
            {},
            {},
            {},
            {
                text: (stats.total_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                bold: true,
                fontSize: 10,
                alignment: 'right',
                fillColor: '#f0f0f0'
            },
            { text: `${stats.total_expenses || 0} รายการ`, bold: true, fontSize: 10, alignment: 'center', fillColor: '#f0f0f0' }
        ]);
    }

    const docDefinition = {
        pageSize: 'A4',
        pageOrientation: 'landscape', // แนวนอนเพื่อให้ตารางกว้างขึ้น
        pageMargins: [40, 80, 40, 60],
        defaultStyle: {
            font: 'THSarabunNew',
            fontSize: 14
        },
        header: function (currentPage, pageCount) {
            return {
                columns: [
                    {
                        width: 100,
                        ...(logoBase64 ? {
                            image: logoBase64,
                            width: 60,
                            height: 60,
                            margin: [40, 10, 0, 0]
                        } : {
                            text: '',
                            margin: [0, 0, 0, 0]
                        })
                    },
                    {
                        width: '*',
                        stack: [
                            { text: company.company_name || 'ชื่อบริษัท', fontSize: 16, bold: true, alignment: 'center', margin: [0, 15, 0, 2] },
                            { text: company.address || '', fontSize: 10, alignment: 'center', margin: [0, 0, 0, 2] },
                            { text: `โทร: ${company.phone || '-'} | เลขประจำตัวผู้เสียภาษี: ${company.tax_id || '-'}`, fontSize: 10, alignment: 'center' }
                        ]
                    },
                    {
                        width: 100,
                        text: ''
                    }
                ],
                margin: [0, 0, 0, 10]
            };
        },
        footer: function (currentPage, pageCount) {
            return {
                text: `หน้า ${currentPage} จาก ${pageCount} | พิมพ์เมื่อ: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
                alignment: 'center',
                fontSize: 9,
                margin: [0, 10, 0, 0]
            };
        },
        styles: {
            header: {
                fontSize: 18,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            subheader: {
                fontSize: 12,
                margin: [0, 5, 0, 10]
            },
            tableHeader: {
                bold: true,
                fontSize: 10,
                color: 'white',
                fillColor: '#1890ff',
                alignment: 'center'
            },
            summaryBox: {
                fillColor: '#f0f0f0',
                margin: [0, 10, 0, 10]
            }
        },
        content: [
            // หัวข้อรายงาน
            {
                text: 'รายงานค่าใช้จ่าย',
                style: 'header'
            },

            // ข้อมูลการกรอง
            {
                columns: [
                    {
                        width: '*',
                        text: [
                            { text: filterText, fontSize: 11, bold: true },
                            filterDetails.length > 0 ? '\n' + filterDetails.join(' | ') : ''
                        ]
                    },
                    {
                        width: 'auto',
                        text: `วันที่พิมพ์: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
                        fontSize: 10,
                        alignment: 'right'
                    }
                ],
                margin: [0, 0, 0, 15]
            },

            // สถิติสรุป
            {
                columns: [
                    {
                        width: '25%',
                        stack: [
                            { text: 'ยอดรวมค่าใช้จ่าย', fontSize: 10, color: '#666' },
                            { text: `${(stats.total_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท`, fontSize: 14, bold: true, color: '#cf1322' }
                        ]
                    },
                    {
                        width: '25%',
                        stack: [
                            { text: 'จำนวนรายการ', fontSize: 10, color: '#666' },
                            { text: `${stats.total_expenses || 0} รายการ`, fontSize: 14, bold: true, color: '#1890ff' }
                        ]
                    },
                    {
                        width: '25%',
                        stack: [
                            { text: 'อนุมัติแล้ว', fontSize: 10, color: '#666' },
                            { text: `${(stats.approved_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท`, fontSize: 14, bold: true, color: '#52c41a' }
                        ]
                    },
                    {
                        width: '25%',
                        stack: [
                            { text: 'รอดำเนินการ', fontSize: 10, color: '#666' },
                            { text: `${(stats.pending_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท`, fontSize: 14, bold: true, color: '#faad14' }
                        ]
                    }
                ],
                margin: [0, 0, 0, 15]
            },

            // ตารางรายการ
            {
                table: {
                    headerRows: 1,
                    widths: ['auto', 'auto', 'auto', 'auto', '*', 'auto', 'auto'],
                    body: tableBody
                },
                layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                        if (rowIndex === 0) return '#1890ff';
                        if (rowIndex === node.table.body.length - 1) return '#f0f0f0';
                        return (rowIndex % 2 === 0) ? '#f9f9f9' : null;
                    },
                    hLineWidth: function (i, node) {
                        return (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.5;
                    },
                    vLineWidth: function (i) {
                        return 0.5;
                    },
                    hLineColor: function (i) {
                        return '#ddd';
                    },
                    vLineColor: function (i) {
                        return '#ddd';
                    }
                }
            }
        ]
    };

    try {
        const pdfDocGenerator = window.pdfMake.createPdf(docDefinition);

        return new Promise((resolve, reject) => {
            pdfDocGenerator.getDataUrl((dataUrl) => {
                if (dataUrl) resolve(dataUrl);
                else reject(new Error('ไม่สามารถสร้าง PDF ได้'));
            });
        });
    } catch (error) {
        console.error('Error generating expenses table PDF:', error);
        throw new Error('ไม่สามารถสร้าง PDF ได้: ' + error.message);
    }
};

/**
 * ดาวน์โหลด PDF ตารางรายการค่าใช้จ่าย
 */
export const downloadExpensesTablePDF = async (expenses, company = {}, filters = {}, stats = {}) => {
    const dataUrl = await generateExpensesTablePDF(expenses, company, filters, stats);

    // สร้างชื่อไฟล์
    const dateStr = dayjs().format('YYYYMMDD_HHmm');
    const fileName = `รายงานค่าใช้จ่าย_${dateStr}.pdf`;

    // ดาวน์โหลด
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    link.click();

    return { success: true, fileName };
};
